/**
 * 商品数据处理工具模块
 * 负责商品数据的格式化、过滤和计算
 */

import { PRODUCT_TYPE } from '../config/api';

// 商店主奖励类型映射（按后端 Rewards.MainType）
// 金币商品使用 MainType=1，钻石商品使用 MainType=3
const PRODUCT_REWARD_MAIN_TYPE = {
  [PRODUCT_TYPE.NEW_STORE_COIN]: 1,
  [PRODUCT_TYPE.BASE_COIN]: 1,
  [PRODUCT_TYPE.NEW_STORE_GEM]: 3,
  [PRODUCT_TYPE.BASE_GEM]: 3,
};

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getProductRewardMainType = (productType) => {
  const normalizedType = toSafeNumber(productType);
  return PRODUCT_REWARD_MAIN_TYPE[normalizedType] ?? null;
};

const getBackendRewardsList = (product) => {
  if (Array.isArray(product?.rawProduct?.Rewards)) {
    return product.rawProduct.Rewards;
  }

  if (Array.isArray(product?.Rewards)) {
    return product.Rewards;
  }

  if (Array.isArray(product?.rewards)) {
    return product.rewards;
  }

  return [];
};

export const getBackendRewardAmount = (product) => {
  const productType = product?.productType ?? product?.ProductType;
  const targetMainType = getProductRewardMainType(productType);
  if (targetMainType === null) return 0;

  return getBackendRewardsList(product).reduce((sum, reward) => {
    const rewardMainType = toSafeNumber(reward?.MainType ?? reward?.mainType);
    if (rewardMainType !== targetMainType) return sum;

    return sum + toSafeNumber(
      reward?.Value ??
      reward?.value ??
      reward?.Count ??
      reward?.count ??
      reward?.Amount ??
      reward?.amount
    );
  }, 0);
};

/**
 * 格式化数字为千分位显示
 * @param {number} num - 要格式化的数字
 * @returns {string} 格式化后的字符串，如 "1,000,000"
 */
export const formatNumber = (num) => {
  const value = Number(num);
  if (isNaN(value)) return '0';
  return value.toLocaleString('en-US');
};

/**
 * 根据商品类型获取对应的图标路径
 * @param {number} productType - 商品类型 (1100=金币促销, 1101=金币基准, 1200=钻石促销, 1201=钻石基准, 1300=道具)
 * @returns {string} 图标路径
 */
export const getProductImage = (productType) => {
  switch (productType) {
    case PRODUCT_TYPE.NEW_STORE_COIN:
    case PRODUCT_TYPE.BASE_COIN:
      return 'assets/coin/coin3.webp';
    case PRODUCT_TYPE.NEW_STORE_GEM:
    case PRODUCT_TYPE.BASE_GEM:
      return 'assets/gem/gem.webp';
    case PRODUCT_TYPE.NEW_STORE_BOOST:
      return 'assets/boost/boost.webp';
    default:
      return 'assets/coin/coin3.webp';
  }
};

/**
 * 根据商品类型获取类型名称
 * @param {number} productType - 商品类型
 * @returns {string} 类型名称
 */
export const getProductTypeName = (productType) => {
  switch (productType) {
    case PRODUCT_TYPE.NEW_STORE_GEM:
    case PRODUCT_TYPE.BASE_GEM:
      return 'Gems';
    case PRODUCT_TYPE.NEW_STORE_BOOST:
      return 'Boost';
    case PRODUCT_TYPE.NEW_STORE_COIN:
    case PRODUCT_TYPE.BASE_COIN:
    default:
      return 'Coins';
  }
};

/**
 * 计算商品促销百分比（向下取整）
 * 公式：((现值 - 划线值) / 划线值) * 100
 *
 * @param {object} product - 格式化后的商品
 * @returns {number} 促销百分比，最小为 0
 */
export const getProductPromoPercent = (product) => {
  if (!product) return 0;

  const isBaseProduct = product.productType === PRODUCT_TYPE.BASE_COIN ||
    product.productType === PRODUCT_TYPE.BASE_GEM;
  if (isBaseProduct) return 0;

  const baseValue = Number(product.baseValue);
  const serverReward = getBackendRewardAmount(product);

  if (!Number.isFinite(baseValue) || !Number.isFinite(serverReward) || baseValue <= 0) {
    return 0;
  }

  const percent = Math.floor(((serverReward - baseValue) / baseValue) * 100);
  return percent > 0 ? percent : 0;
};

/**
 * 解析商品扩展信息
 * @param {object} product - 原始商品数据
 * @returns {object} 解析后的扩展信息
 */
const parseProductExtra = (product) => {
  let extra = {};
  let webExtra = {};

  try {
    extra = JSON.parse(product.Extra || '{}');
  } catch (e) {
    extra = {};
  }

  try {
    webExtra = JSON.parse(product.WebExtra || '{}');
  } catch (e) {
    webExtra = {};
  }

  return { extra, webExtra };
};

/**
 * 格式化单个商品数据
 * 将后端返回的原始数据转换为前端可用的格式
 *
 * @param {object} product - 后端返回的原始商品数据
 * @returns {object|null} 格式化后的商品数据，如果输入无效则返回 null
 */
export const formatProduct = (product) => {
  // 空值检查
  if (!product) {
    console.warn('formatProduct received null product');
    return null;
  }

  // 解析扩展信息
  const { extra, webExtra } = parseProductExtra(product);

  // 数值字段：严格使用后端新字段 BaseValue2（不保留旧字段兜底）
  const baseValue = toSafeNumber(product.BaseValue2);
  const rewardsList = Array.isArray(product.Rewards) ? product.Rewards : [];
  const serverReward = getBackendRewardAmount(product);

  // 促销比例按服务端增量计算
  // extraRate = round((serverReward - baseValue) / baseValue * 100)
  const extraRate = baseValue > 0
    ? Math.round(((serverReward - baseValue) / baseValue) * 100)
    : 0;

  // 从 WebExtra 获取 PayPal 价格和原价
  const paypalPrice = webExtra.webPrize || product.Price;
  const originalPrice = webExtra.webCrossedPrize || product.Price;

  // 格式化商品标题（替换模板变量）
  const description = (product.Title || '')
    .replace('{{.base}}', formatNumber(baseValue));

  return {
    // 基础信息
    id: product.ProductID,
    name: `$${product.Price}`,
    description,

    // 价格信息
    price: product.Price,
    originalPrice,
    paypalPrice,

    // 数值信息
    baseValue,
    finalValue: serverReward,
    serverReward,
    extraRate,

    // 类型信息
    productType: product.ProductType,
    productSubType: product.ProductSubType,
    productLevel: product.ProductLevel,

    // 奖励和扩展信息
    rewards: rewardsList,
    extra,
    webExtra,
    rawProduct: product,

    // 关联ID
    salesId: product.SalesId,
    luckySpinProductId: product.luckSpinProductID,
    fantasyProductId: product.FantasyProductId,

    // 标签信息
    isHot: extra.hotoffers === 1 || extra.hotoffers === 4,
    isBest: extra.hotoffers === 2 || extra.hotoffers === 3,

    // 图片
    image: getProductImage(product.ProductType),
  };
};

/**
 * 按类型分类商品列表
 * @param {array} products - 原始商品列表
 * @returns {object} 分类后的商品对象 { coinProducts, gemProducts, boostProducts, hotOfferProducts, baseCoinProducts, baseGemProducts }
 */
export const categorizeProducts = (products) => {
  // 过滤掉无效数据
  const validProducts = products.filter((p) => p && p.ProductType != null);

  // 按类型分类
  // 金币促销商品（类型 1100）
  const coinProducts = validProducts.filter((p) => p.ProductType === PRODUCT_TYPE.NEW_STORE_COIN);
  // 金币基准商品（类型 1101）- 不参与活动折扣
  const baseCoinProducts = validProducts.filter((p) => p.ProductType === PRODUCT_TYPE.BASE_COIN);
  // 钻石促销商品（类型 1200）
  const gemProducts = validProducts.filter((p) => p.ProductType === PRODUCT_TYPE.NEW_STORE_GEM);
  // 钻石基准商品（类型 1201）- 不参与活动折扣
  const baseGemProducts = validProducts.filter((p) => p.ProductType === PRODUCT_TYPE.BASE_GEM);
  // 道具商品（类型 1300）
  const boostProducts = validProducts.filter((p) => p.ProductType === PRODUCT_TYPE.NEW_STORE_BOOST);

  // Hot Offers 商品（有 hotoffers 标记的）
  const hotOfferProducts = validProducts.filter((p) => {
    try {
      const extra = JSON.parse(p.Extra || '{}');
      return extra.hotoffers && extra.hotoffers > 0;
    } catch (e) {
      return false;
    }
  });

  return {
    coinProducts,
    baseCoinProducts,
    gemProducts,
    baseGemProducts,
    boostProducts,
    hotOfferProducts,
  };
};

/**
 * 格式化并分类所有商品
 * 主入口函数，一次性处理所有商品数据
 *
 * @param {array} products - 后端返回的原始商品列表
 * @returns {object} 格式化并分类后的商品数据
 */
export const formatAndCategorizeProducts = (products) => {
  // 先分类
  const {
    coinProducts,
    baseCoinProducts,
    gemProducts,
    baseGemProducts,
    boostProducts,
    hotOfferProducts,
  } = categorizeProducts(products);

  // 格式化各类商品
  const formattedCoinProducts = coinProducts
    .map(formatProduct)
    .filter((p) => p !== null);

  const formattedBaseCoinProducts = baseCoinProducts
    .map(formatProduct)
    .filter((p) => p !== null);

  const formattedGemProducts = gemProducts
    .map(formatProduct)
    .filter((p) => p !== null);

  const formattedBaseGemProducts = baseGemProducts
    .map(formatProduct)
    .filter((p) => p !== null);

  const formattedBoostProducts = boostProducts
    .map(formatProduct)
    .filter((p) => p !== null);

  const formattedHotOfferProducts = hotOfferProducts
    .map(formatProduct)
    .filter((p) => p !== null);

  // 所有商品格式化后的列表
  const allFormattedProducts = products
    .map(formatProduct)
    .filter((p) => p !== null);

  return {
    // 所有商品
    products: allFormattedProducts,

    // 分类商品
    coinProducts: formattedCoinProducts,
    baseCoinProducts: formattedBaseCoinProducts,
    gemProducts: formattedGemProducts,
    baseGemProducts: formattedBaseGemProducts,
    boostProducts: formattedBoostProducts,
    hotOfferProducts: formattedHotOfferProducts,
  };
};
